/**
 * Event/date strings from APIs (YYYY-MM-DD, ISO datetimes, plain HH:mm) — use Date instead of regex.
 */

export function parseEventDateTime(input: string | Date | null | undefined): Date | null {
  if (input == null || input === '') return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;

  const s = String(input).trim();
  const tIdx = s.indexOf('T');
  const datePart = tIdx === -1 ? s : s.slice(0, tIdx);
  const parts = datePart.split('-');

  if (parts.length >= 3) {
    const y = parseInt(parts[0], 10);
    const mo = parseInt(parts[1], 10);
    const da = parseInt(parts[2], 10);
    if (!isNaN(y) && !isNaN(mo) && !isNaN(da) && String(parts[0]).length === 4) {
      if (tIdx !== -1) {
        const full = new Date(s);
        if (!isNaN(full.getTime())) return full;
      }
      return new Date(y, mo - 1, da);
    }
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Parse API "time of day" often sent as 0000-01-01THH:mm:ssZ (.NET / SQL time-as-datetime).
 * Uses UTC wall clock when string ends with Z; else local calendar time.
 */
function parseClockFromIsoOrDateTime(s: string): Date | null {
  const trim = s.trim();
  const full = new Date(trim);
  const invalid = isNaN(full.getTime());
  const year = invalid ? NaN : full.getFullYear();
  const sentinelOrBadYear = /^0000-01-01T/i.test(trim) || (!invalid && year < 1000) || invalid;
  if (sentinelOrBadYear) {
    const m = trim.match(/T(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return null;
    const hh = parseInt(m[1], 10);
    const mi = parseInt(m[2], 10);
    const sc = m[3] ? parseInt(m[3], 10) : 0;
    if (/Z$/i.test(trim)) {
      return new Date(Date.UTC(1970, 0, 1, hh, mi, sc));
    }
    return new Date(1970, 0, 1, hh, mi, sc);
  }
  return full;
}

/** 12-hour time e.g. "6:00 PM" — from ISO string, "HH:mm", or "HH:mm:ss". */
export function formatTimeLabelEn(input: string | null | undefined): string {
  if (input == null || input === '') return '';
  const s = String(input).trim();
  if (s.includes('T')) {
    const d = parseClockFromIsoOrDateTime(s);
    if (d && !isNaN(d.getTime())) {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }
  const colon = s.indexOf(':');
  if (colon > 0) {
    const h = parseInt(s.slice(0, colon), 10);
    const rest = s.slice(colon + 1);
    const sec = rest.indexOf(':');
    const minStr = sec === -1 ? rest.slice(0, 2) : rest.slice(0, sec);
    const mi = parseInt(minStr, 10) || 0;
    if (!isNaN(h) && !isNaN(mi)) {
      return new Date(1970, 0, 1, h, mi).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  }
  return s;
}

/**
 * Sidebar date line: single day, same month range, or both month names when months (or years) differ.
 */
export function formatEventDateRangeLine(start: Date, end: Date, locale = 'en-US'): string {
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
  const sy = start.getFullYear();
  const sm = start.getMonth();
  const sd = start.getDate();
  const ey = end.getFullYear();
  const em = end.getMonth();
  const ed = end.getDate();

  if (sy === ey && sm === em && sd === ed) {
    return start.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
  }

  const monthFmt = new Intl.DateTimeFormat(locale, { month: 'long' });
  const full: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };

  if (sy === ey && sm === em) {
    return `${monthFmt.format(start)} ${sd}-${ed}, ${sy}`;
  }

  if (sy === ey) {
    return `${monthFmt.format(start)} ${sd} – ${monthFmt.format(end)} ${ed}, ${sy}`;
  }

  return `${start.toLocaleDateString(locale, full)} – ${end.toLocaleDateString(locale, full)}`;
}

/**
 * Clock range: always "start – end" (hour + minute, 12h, no seconds).
 */
export function formatRangeClockPair(start: Date, end: Date, locale = 'en-US'): string {
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
  return `${start.toLocaleTimeString(locale, opts)} – ${end.toLocaleTimeString(locale, opts)}`;
}

/**
 * Booking/tickets: which calendar day to select from URL + router state.
 * `date` (return from tickets) beats `returnDate` state, then `start` (event-detail entry).
 */
export function resolveBookingActiveDateStr(
  qp: { get(name: string): string | null },
  navState?: Record<string, unknown> | null
): string | null {
  const dateQ = qp.get('date');
  if (dateQ) {
    const n = toLocalYyyyMmDd(dateQ);
    if (n) return n;
  }
  const ret = navState?.['returnDate'];
  if (typeof ret === 'string' && ret.trim()) {
    const n = toLocalYyyyMmDd(ret.trim());
    if (n) return n;
  }
  const startQ = qp.get('start');
  if (startQ) {
    const n = toLocalYyyyMmDd(startQ);
    if (n) return n;
  }
  return null;
}

/** Calendar date YYYY-MM-DD in local components (no UTC shift for date-only inputs). */
export function toLocalYyyyMmDd(input: string | Date): string {
  const d = typeof input === 'string' ? parseEventDateTime(input) : input;
  if (!d || isNaN(d.getTime())) return '';
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

/** API often uses 0000-01-01T… for “time only”; never use as calendar date. */
function isSentinelCalendarPlaceholder(s: string): boolean {
  return /^0000-01-01T/i.test(s.trim());
}

function pickEventStartString(e: Record<string, unknown>): string {
  const keys = [
    'start_date',
    'event_date',
    'show_date',
    'event_start_date',
    'date',
    'startDate'
  ] as const;
  for (const k of keys) {
    const v = e[k];
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (!s || isSentinelCalendarPlaceholder(s)) continue;
    return s;
  }
  return '';
}

const EVENT_DATE_KEYS_FOR_CLOCK = [
  'start_date',
  'event_date',
  'show_date',
  'event_start_date',
  'date',
  'startDate'
] as const;

/** Clock from first raw date-like field with a time part (includes 0000-01-01T… when pick skipped it for calendar). */
function clockFromFirstRawDateLikeField(e: Record<string, unknown>): string {
  for (const k of EVENT_DATE_KEYS_FOR_CLOCK) {
    const v = e[k];
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (!s.includes('T')) continue;
    const t = formatTimeLabelEn(s);
    if (t) return t;
  }
  return '';
}

function clockFromEndDateField(e: Record<string, unknown>): string {
  const v = e['end_date'];
  if (v == null || v === '') return '';
  const s = String(v).trim();
  if (!s.includes('T')) return '';
  return formatTimeLabelEn(s) || '';
}

function timeFromFieldRecord(v: unknown): string {
  if (v == null || v === '') return '';
  return formatTimeLabelEn(String(v));
}

/**
 * Single source of truth for event date lines (matches event-detail / preview cards).
 * Uses start_date → event_date → show_date → date; end_date for ranges.
 */
export function formatEventDateLineFromRecord(
  event: Record<string, unknown> | null | undefined,
  locale = 'en-US'
): string {
  if (!event) return '';
  const startStr = pickEventStartString(event);
  if (!startStr) return '';
  const start = parseEventDateTime(startStr);
  if (!start || isNaN(start.getTime()) || start.getFullYear() < 1000) return '';

  const endRaw = event['end_date'];
  if (endRaw == null || String(endRaw).trim() === '' || isSentinelCalendarPlaceholder(String(endRaw))) {
    return start.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
  }
  const end = parseEventDateTime(String(endRaw));
  if (!end || isNaN(end.getTime()) || end.getFullYear() < 1000) {
    return start.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
  }
  return formatEventDateRangeLine(start, end, locale);
}

/**
 * Time line matching event-detail: explicit slot `start_time`/`end_time` (booking) win over
 * `start_date`+`end_date` ISO pairs — otherwise show_date "…T00:00:00Z" steals the range.
 */
export function formatEventTimeLineFromRecord(
  event: Record<string, unknown> | null | undefined,
  locale = 'en-US'
): string {
  if (!event) return '';

  const startFromFields = timeFromFieldRecord(
    event['start_time'] ??
      event['time'] ??
      event['show_time'] ??
      event['event_start_time'] ??
      event['startTime']
  );
  const endFromFields = timeFromFieldRecord(
    event['end_time'] ??
      event['show_end_time'] ??
      event['event_end_time'] ??
      event['close_time'] ??
      event['endTime']
  );

  if (startFromFields && endFromFields) {
    return `${startFromFields} – ${endFromFields}`;
  }

  const startStr = pickEventStartString(event);
  const endStr = event['end_date'];

  if (
    startStr &&
    endStr != null &&
    String(endStr) !== '' &&
    String(startStr).includes('T') &&
    String(endStr).includes('T')
  ) {
    const startS = String(startStr);
    const endS = String(endStr);
    const bothSentinel =
      isSentinelCalendarPlaceholder(startS) && isSentinelCalendarPlaceholder(endS);
    if (bothSentinel) {
      const a = formatTimeLabelEn(startS);
      const b = formatTimeLabelEn(endS);
      if (a && b) return `${a} – ${b}`;
    } else if (!isSentinelCalendarPlaceholder(startS) && !isSentinelCalendarPlaceholder(endS)) {
      const ds = parseEventDateTime(startS);
      const de = parseEventDateTime(endS);
      if (ds && de && !isNaN(ds.getTime()) && !isNaN(de.getTime())) {
        return formatRangeClockPair(ds, de, locale);
      }
    }
  }

  const startFromDate = clockFromFirstRawDateLikeField(event);
  const endFromDate = clockFromEndDateField(event);
  const startLabel = startFromFields || startFromDate;
  const endLabel = endFromFields || endFromDate;

  if (startLabel && endLabel) {
    return `${startLabel} – ${endLabel}`;
  }
  if (startLabel) return startLabel;
  if (endLabel) return endLabel;

  if (startStr) {
    const date = parseEventDateTime(String(startStr));
    if (date && !isNaN(date.getTime()) && date.getFullYear() >= 1000) {
      return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    const clock = formatTimeLabelEn(String(startStr));
    if (clock) return clock;
  }
  return '';
}

/**
 * One line for compact UI: same date + time rules as event-detail metadata, joined with " · ".
 */
export function formatEventDateTimeLineFromRecord(
  event: Record<string, unknown> | null | undefined,
  locale = 'en-US'
): string {
  const dateLine = formatEventDateLineFromRecord(event, locale);
  const timeLine = formatEventTimeLineFromRecord(event, locale);
  return [dateLine, timeLine].filter(Boolean).join(' · ');
}

function nonemptyTrim(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

/**
 * Review / checkout: selected `show` + cart `date`/`time` must win over API `event.start_date`.
 * Use show `start_time` / `end_time` (incl. 0000-01-01T… slot times from booking) — same as booking labels.
 */
export function formatEventDateTimeLineFromCheckout(
  checkout: Record<string, unknown> | null | undefined,
  locale = 'en-US'
): string {
  if (!checkout) return '';
  const ev = (checkout['event'] as Record<string, unknown>) ?? {};
  const show = (checkout['show'] as Record<string, unknown>) ?? {};
  const sd =
    nonemptyTrim(show['show_date']) ??
    nonemptyTrim(checkout['date']) ??
    nonemptyTrim(ev['start_date']) ??
    nonemptyTrim(ev['event_date']) ??
    nonemptyTrim(ev['event_start_date']) ??
    nonemptyTrim(ev['date']);
  const ed = nonemptyTrim(show['end_date']) ?? nonemptyTrim(ev['end_date']);
  const startTime =
    show['start_time'] ??
    show['startTime'] ??
    ev['start_time'] ??
    ev['startTime'] ??
    ev['time'] ??
    checkout['time'];
  const endTime =
    show['end_time'] ?? show['endTime'] ?? ev['end_time'] ?? ev['endTime'];

  const rec: Record<string, unknown> = {
    ...ev,
    start_date: sd ?? ev['start_date'],
    end_date: ed ?? ev['end_date'],
    start_time: startTime,
    end_time: endTime
  };
  return formatEventDateTimeLineFromRecord(rec, locale);
}
